/**
 * ==================== 视频图层模块 - 元数据解析与缓存 ====================
 *
 * 职责：视频元数据的解析、缓存和对外信息提取，纯数据层无 Canvas 依赖
 *
 * 依赖：./types（VideoLayerMetaV1、VideoLayerInfo）
 * 被依赖：FabricVideo.ts（_render 中使用 getVideoLayerInfo）
 */

import { objExt } from '@xmiles/holopix-helper'
import type { VideoLayerMetaV1, VideoLayerInfo } from './types'

/** 视频元数据缓存条目（利用 fabric.Object 实例存储解析结果，避免重复解析） */
type VideoMetaCache = { raw: string; meta: VideoLayerMetaV1 | null }

/**
 * 解析视频图层的 extraData 字段为结构化元数据
 *
 * @param extraData - fabric.Object 上的 extraData 字段（JSON 字符串）
 * @returns 解析成功返回 VideoLayerMetaV1，否则返回 null
 */
export const parseVideoData = (extraData: unknown): VideoLayerMetaV1 | null => {
  if (typeof extraData !== 'string' || !extraData) return null
  try {
    const obj = objExt.parse<any>(extraData, null)
    if (!obj) return null

    const v = obj.v
    const videoUrl = obj.videoUrl ?? obj.video_url
    const coverUrl = obj.coverUrl ?? obj.cover_url
    if (typeof videoUrl !== 'string') return null
    if (typeof coverUrl !== 'string') return null

    if (v !== undefined && v !== 1) return null
    return {
      v: 1,
      videoUrl,
      coverUrl,
      duration: Number(obj.duration) || 0,
      title: typeof obj.title === 'string' ? obj.title : '',
    }
  } catch {
    return null
  }
}

/**
 * 获取视频图层的缓存元数据
 *
 * 利用 __videoMetaCache 属性在 fabric.Object 实例上缓存解析结果，
 * 当 extraData 未变更时直接返回缓存，避免重复 JSON.parse
 *
 * @param layer - fabric.Object 实例（带 extraData 字段）
 * @returns 缓存的 VideoLayerMetaV1 或 null
 */
const getCachedVideoMeta = (layer: any): VideoLayerMetaV1 | null => {
  const extraData = layer?.extraData
  if (typeof extraData !== 'string' || !extraData) {
    if (layer) (layer as any).__videoMetaCache = { raw: '', meta: null } as VideoMetaCache
    return null
  }

  const cache = (layer as any).__videoMetaCache as VideoMetaCache | undefined
  if (cache && cache.raw === extraData) return cache.meta

  const meta = parseVideoData(extraData)
  ;(layer as any).__videoMetaCache = { raw: extraData, meta } as VideoMetaCache
  return meta
}

/**
 * 判断一个 fabric.Object 是否为视频图层
 *
 * 内联实现避免从 factory.ts 导入造成的循环依赖（factory → meta 是预期方向）
 */
const isVideoLayer = (obj: any): boolean => {
  return obj?.type === 'video'
}

/**
 * 从 fabric.Object 中提取视频图层信息
 *
 * 综合 extraData 元数据和图层属性（name、src），
 * 生成完整的 VideoLayerInfo 供 UI 渲染使用
 *
 * @param obj - fabric.Object 实例
 * @returns VideoLayerInfo 或 null（非视频图层时）
 */
export const getVideoLayerInfo = (obj: any): VideoLayerInfo | null => {
  if (!obj || !isVideoLayer(obj)) return null
  const layer = obj as any
  const meta = getCachedVideoMeta(layer)
  const coverUrl = meta?.coverUrl || layer.src || layer.originalSrc || ''
  const isAutoName = (value: unknown) =>
    typeof value === 'string' && /^video_\d+$/i.test(value.trim())
  const layerName = typeof layer.name === 'string' ? layer.name : ''
  const title =
    meta?.title && isAutoName(layerName)
      ? meta.title
      : layerName || meta?.title || ''
  return {
    videoUrl: meta?.videoUrl || '',
    coverUrl,
    duration: meta?.duration || 0,
    title,
  }
}
